import { useMutation, useQueryClient } from '@tanstack/react-query';
import UserService from '../Services/users/UserService';
import AccountService from '../Services/users/AccountService';
import NotificationService from '../Services/users/NotificationService';
import { toast } from 'sonner';
import { GET_USERS_QUERY_KEY } from '../queries/useGetUsers';
import { GET_ACCOUNT_DEVICES_QUERY_KEY, GET_NOTIFICATIONS_QUERY_KEY } from '../queries/useGetUsers';


export const useUserMutations = () => {
  const queryClient = useQueryClient();

  const createUserMutation = useMutation({
    mutationFn: (userData) => UserService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries([GET_USERS_QUERY_KEY]);
      toast.success("Utilisateur créé avec succès");
    },
    onError: (error) => {
      toast.error(`Échec de la création : ${error.message}`);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, userData }) => UserService.updateUser(id, userData),
    onSuccess: () => {
      queryClient.invalidateQueries([GET_USERS_QUERY_KEY]);
      toast.success("Utilisateur mis à jour avec succès");
    },
    onError: (error) => {
      toast.error(`Échec de la mise à jour : ${error.message}`);
    },
  });

  const updateAvatarMutation = useMutation({
    mutationFn: ({ id, avatarFile }) => AccountService.updateAvatar(id, avatarFile),
    onSuccess: () => {
      queryClient.invalidateQueries([GET_USERS_QUERY_KEY]);
      toast.success("Avatar mis à jour avec succès");
    },
    onError: (error) => {
      toast.error(`Échec de la mise à jour de l'avatar : ${error.message}`);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id) => UserService.deleteUser(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries([GET_USERS_QUERY_KEY]);
        toast.success(response.message);
      } else {
        toast.error("Une erreur s'est produite lors de la suppression de l'utilisateur.");
      }
    },
    onError: (error) => {
      toast.error(`Échec de la suppression : ${error.message}`);
    },
  });

  const updatePersonalInfoMutation = useMutation({
    mutationFn: (userInfo) => AccountService.updatePersonalInfo(userInfo),
    onSuccess: () => {
      queryClient.invalidateQueries([GET_USERS_QUERY_KEY]);
      toast.success("Informations personnelles mises à jour avec succès");
    },
    onError: (error) => {
      toast.error(`Échec de la mise à jour : ${error.message}`);
    },
  });

  const checkUserPasswordMutation = useMutation({
    mutationFn: (password) => AccountService.checkUserPassword(password),
    onSuccess: (data) => {
      if (data) {
        toast.success("Mot de passe correct");
      } else {
        toast.error("Mot de passe incorrect");
      }
    },
    onError: (error) => {
      toast.error(`Échec de la vérification du mot de passe : ${error.message}`);
    },
  });

  const deleteUserDeviceMutation = useMutation({
    mutationFn: (deviceIdentifier) => AccountService.deleteUserDevice(deviceIdentifier),
    onSuccess: () => {
      queryClient.invalidateQueries([GET_ACCOUNT_DEVICES_QUERY_KEY]);
      toast.success("Appareil supprimé avec succès");
    },
    onError: (error) => {
      toast.error(`Échec de la suppression de l'appareil : ${error.message}`);
    },
  });

  const changeUserPasswordMutation = useMutation({
    mutationFn: (passwordData) => AccountService.changeUserPassword(passwordData),
    onSuccess: () => {
      toast.success("Mot de passe changé avec succès");
    },
    onError: (error) => {
      toast.error(`Échec du changement de mot de passe : ${error.message}`);
    },
  });

  const updateUserEducationalLevelMutation = useMutation({
    mutationFn: (educationalLevelId) => AccountService.updateUserEducationalLevel(educationalLevelId),
    onSuccess: () => {
      toast.success("Niveau d'éducation mis à jour avec succès");
    },
    onError: (error) => {
      toast.error(`Échec de la mise à jour : ${error.message}`);
    },
  });

  const updateUserEmailMutation = useMutation({
    mutationFn: (email) => AccountService.updateUserEmail(email),
    onSuccess: () => {
      toast.success("Email mis à jour avec succès. Veuillez vérifier votre nouvel email.");
    },
    onError: (error) => {
      toast.error(`Échec de la mise à jour : ${error.message}`);
    },
  });

  const markNotificationAsReadMutation = useMutation({
    mutationFn: (id) => NotificationService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries([GET_NOTIFICATIONS_QUERY_KEY]);
      // toast.success("Notification marquée comme lue");
    },
    onError: (error) => {
      // toast.error(`Échec de la mise à jour : ${error.message}`);
    },
  });

  const markAllNotificationsAsReadMutation = useMutation({
    mutationFn: () => NotificationService.markAllAsRead(),
    onSuccess: (message) => {
      queryClient.invalidateQueries([GET_NOTIFICATIONS_QUERY_KEY]);
      toast.success(message || "Toutes les notifications ont été marquées comme lues");
    },
    onError: (error) => {
      toast.error(`Échec du marquage de toutes les notifications : ${error.message}`);
    },
  });

  return {
    createUserMutation,
    updateUserMutation,
    deleteUserMutation,
    updateAvatarMutation,
    updatePersonalInfoMutation,
    checkUserPasswordMutation,
    deleteUserDeviceMutation,
    changeUserPasswordMutation,
    updateUserEducationalLevelMutation,
    updateUserEmailMutation,
    markNotificationAsReadMutation,
    markAllNotificationsAsReadMutation,
  };
};